/* A panel over the page for editing a sheet section, levelling up or confirming.
   Escape or the backdrop closes it; focus moves into it when it opens. */

import { ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface Props {
  title: string;
  onClose: () => void;
  /** "alertdialog" for a question that needs an answer. */
  role?: 'dialog' | 'alertdialog';
  footer?: ReactNode;
  children: ReactNode;
}

export default function Dialog({ title, onClose, role = 'dialog', footer, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Focus moves in once, on opening, so typing in a field inside is never interrupted.
  useEffect(() => ref.current?.focus(), []);

  useEffect(() => {
    // Only keys pressed inside, so Escape in a question over another dialog closes just the question.
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && ref.current?.contains(e.target as Node) && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cb-dialog-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className={`cb-dialog cb-dialog--${role}`}
        role={role}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cb-dialog__head">
          <h2 className="cb-dialog__title">{title}</h2>
          <button type="button" className="cb-dialog__close" aria-label="Close" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="cb-dialog__body">{children}</div>
        {footer && <footer className="cb-dialog__foot">{footer}</footer>}
      </div>
    </div>
  );
}
